import React, { useRef, useEffect, useState } from 'react';
import mapboxgl, { LngLatBounds, LngLatLike, Map as MapBoxMap } from 'mapbox-gl';
import axios from 'axios';
import { useApp } from '../../_libs/hooks/useApp';
import { useBrand } from '../../_libs/hooks/useBrand';
import { circle, bbox, BBox } from '@turf/turf';
import _ from 'lodash';

mapboxgl.accessToken = 'pk.eyJ1IjoiY2FzdGF2cmlkaXMiLCJhIjoiY2w4N3c3ZTI4MDdkNTN1dDVuOW5mcjU4ZCJ9.wgZ3Qhrqa6TYVzKMaixg_g';

interface MapProps {
  geoId?: number;
  radius: number;
  latitude: number;
  longitude: number;
}

const Map: React.FC<MapProps> = ({ geoId, radius, latitude, longitude }) => {
  const mapboxContainer = useRef(null);
  const map = useRef(null);
  const zoom = 20;
  // const [zoom, setZoom] = useState(20);
  const [bounds, setBounds] = useState(null);
  const {
    setGeoIds,
    setGeoId,
  } = useApp();
  const { getColors } = useBrand();
  const colors = getColors();

  useEffect(() => {
    if (map.current) return;

    // Set height of mapContainer
    const mapContainer: HTMLElement = document.getElementById('map-container');
    const mapWidth = mapContainer.clientWidth;
    mapContainer.style.height = `${mapWidth}px`;

    const _map: MapBoxMap = new mapboxgl.Map({
      container: mapboxContainer.current,
      style: 'mapbox://styles/castavridis/cl8v0sjlp009z15mv302p9s9x',
      center: [longitude, latitude],
      zoom: zoom,
    });

    new mapboxgl
      .Marker({
        color: colors?.primary || '#FFFFFF'
      })
      .setLngLat([longitude, latitude])
      .addTo(_map);
      
    _map.on('load', () => {
      if (geoId) {
        // NOTE: Highlight all geoIds w/i view
        // const _geoid = (geoId.toString().length < 12) ? '0' + geoId : geoId;
        // _map.setFilter('B1_Highlight', ['in', 'GEOID', _geoid]);
        // _map.setFilter('B2_Highlight', ['in', 'GEOID', _geoid]);
        // _map.setFilter('B3_Highlight', ['in', 'GEOID', _geoid]);
        // _map.setFilter('B4_Highlight', ['in', 'GEOID', _geoid]);
        // _map.setFilter('B5_Highlight', ['in', 'GEOID', _geoid]);
      } else {

        const container = mapboxContainer.current;

        // Get shape at current lng, lat (seemed off when tried initially)
        const containerWidth = container.offsetWidth;
        const containerHeight = container.offsetHeight;
        const outlineLayers = ['B1_Outlines', 'B2_Outlines', 'B3_Outlines', 'B4_Outlines', 'B5_Outlines'];
        const currentShape = _map.queryRenderedFeatures(
          [containerWidth / 2, containerHeight / 2],
          { layers: outlineLayers }
        );
        if (currentShape.length) {
          const _geoid = currentShape[0].properties.GEOID;

          // NOTE: Highlight all geoIds w/i view
          // _geoid = (_geoid.toString().length < 12) ? '0' + _geoid : _geoid;
          // _map.setFilter('B1_Highlight', ['in', 'GEOID', _geoid]);
          // _map.setFilter('B2_Highlight', ['in', 'GEOID', _geoid]);
          // _map.setFilter('B3_Highlight', ['in', 'GEOID', _geoid]);
          // _map.setFilter('B4_Highlight', ['in', 'GEOID', _geoid]);
          // _map.setFilter('B5_Highlight', ['in', 'GEOID', _geoid]);
          
          setGeoId(parseInt(`${_geoid}`));
        }

        const radiusCircle = circle(
          [longitude, latitude],
          radius,
          {
            steps: 128,
            units: 'miles',
          }
        );
        _map.addLayer({
          'id': 'radius-circle',
          'type': 'fill',
          'source': {
            'type': 'geojson',
            'data': radiusCircle,
          },
          'paint': {
            'fill-color':  colors?.primary || 'rgb(5,69,111)',
            'fill-opacity': 0.0625,
          },
        });
        _map.addLayer({
          'id': 'radius-line',
          'type': 'line',
          'source': {
            'type': 'geojson',
            'data': radiusCircle,
          },
          'paint': {
            'line-color':  colors?.primary || 'rgb(5,69,111)',
            'line-width': 2,
            'line-opacity': 0.65,
          },
        });
        const bufferCircle = circle(
          [longitude, latitude],
          radius * 1.25,
          {
            steps: 64,
            units: 'miles',
          }
        );
        _map.addLayer({
          'id': 'radius-buffer',
          'type': 'line',
          'source': {
            'type': 'geojson',
            'data': bufferCircle,
          },
          'paint': {
            'line-width': 0,
          }
        });

        const boundingBox: BBox = bbox(radiusCircle);
        const swBounds: LngLatLike = [
          boundingBox[0],
          boundingBox[1],
        ];
        const neBounds: LngLatLike = [
          boundingBox[2],
          boundingBox[3],
        ];
        const radiusBounds: LngLatBounds = new LngLatBounds([swBounds, neBounds]);
        // Define app's bounds for querying geoIds
        setBounds(radiusBounds);

        // Add space around radius circle
        const bufferBounds: BBox = bbox(bufferCircle);
        _map.fitBounds([bufferBounds[0], bufferBounds[1], bufferBounds[2], bufferBounds[3]]);

        // const currentShapes = _map.queryRenderedFeatures(
        //   [
        //     [0, 0],
        //     [containerWidth, containerHeight],
        //   ],
        //   { layers: outlineLayers }
        // );
        // _.map(currentShapes, (currShape, index) => {
        //   // Fetch lng,lats for all centroids of visible 
        // });
      }
    });
    // Create a popup, but don't add it to the map yet.
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false
    });

    // _map.on('mouseenter', 'places', (e) => {
    //   // Change the cursor style as a UI indicator.
    //   _map.getCanvas().style.cursor = 'pointer';
       
    //   // Copy coordinates array.
    //   const coordinates = e.features[0].geometry.coordinates.slice();
    //   const description = e.features[0].properties.description;
       
    //   // Ensure that if the map is zoomed out such that multiple
    //   // copies of the feature are visible, the popup appears
    //   // over the copy being pointed to.
    //   while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
    //   coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    //   }
       
    //   // Populate the popup and set its coordinates
    //   // based on the feature found.
    //   popup.setLngLat(coordinates).setHTML(description).addTo(_map);
    // });
       
    _map.on('mouseleave', 'places', () => {
      _map.getCanvas().style.cursor = '';
      popup.remove();
    });

    map.current = _map;

    return (() => {
      // map.current.remove();
    });
  }, []);
  useEffect(() => {
    if (!map.current) { return; }
    map.current.setZoom(zoom);
  }, [zoom]);
  useEffect(() => {
    if (!bounds) { return; }
    axios
      .post('/data_geoIds_within_bounds/', {
        sw: bounds._sw,
        ne: bounds._ne,
      })
      .then((res) => {
        const data = res.data;
        if (data.status === 'OK') {
          setGeoIds(data.geoIds);
        }
      })
      .catch((err) => {
        throw new Error(err);
      })
      .then();
  }, [bounds]);
  return (
    <div id="map-container" className="br-1 border-1-solid--transparent-brand">
      {/* <div className="map-fog-of-war" /> */}
      <div ref={mapboxContainer} className="mapbox-container" />
      {/* <a href="#">View County Risk</a>
      <a href="#">View State Risk</a>
      <a href="#">View Food Insecurity Index</a>
      <a href="#">View Food Security Index</a> */}
    </div>
  );
};

export default Map;
